import { Link } from "react-router-dom";
import {
  ArrowLeft, ChevronLeft, MapPin, Users, Sparkles, Building2, Clock,
} from "lucide-react";

export default function LiveOpportunitySection({ opps }) {
  const primary = opps[0];
  const peek = opps[1];

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="live-opp-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 mb-12 flex-wrap">
          <div className="max-w-xl">
            <div className="text-xs font-bold uppercase tracking-widest text-amber-600">هل أنت مستعد؟</div>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-heading font-black text-slate-900 leading-tight">
              ابدأ من فرص التدريب التعاوني الحقيقية
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              فرص تدريب تعاوني منشورة الآن من جمعيات أو منظمات، وتحقق متطلبات التدريب التعاوني في جامعتك.
            </p>
          </div>
          <Link
            to="/opportunities"
            className="inline-flex items-center gap-1.5 text-teal-700 font-semibold hover:gap-2.5 transition-all"
            data-testid="live-opp-view-all"
          >
            كل الفرص <ChevronLeft className="h-4 w-4" />
          </Link>
        </div>

        {primary ? (
          <div className="grid lg:grid-cols-12 gap-6">
            <article
              className="lg:col-span-8 relative rounded-3xl overflow-hidden border border-cream-200 group"
              data-testid="live-opp-primary"
            >
              <img
                src={primary.cover_image_url || "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1600&q=80"}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700 pointer-events-none"
              />
              <div
                className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/60 to-slate-950/25 pointer-events-none"
                aria-hidden="true"
              />
              {/* Content is in natural DOM flow — no absolute anchoring, no fixed height, no line-clamp.
                  Card GROWS with content on iPhone Safari at Zoom=100%. */}
              <div className="relative z-10 flex flex-col justify-end gap-4 p-5 lg:p-10 min-h-[22rem] sm:min-h-[26rem] lg:min-h-[420px] text-white">
                {primary.category && (
                  <div className="self-start inline-flex max-w-full items-center px-3 py-1.5 rounded-full bg-white text-teal-800 text-xs font-bold shadow-md ring-1 ring-black/5 leading-snug break-words whitespace-normal">
                    {primary.category}
                  </div>
                )}
                <h3 className="text-2xl lg:text-4xl font-heading font-black leading-snug break-words max-w-2xl">
                  {primary.title}
                </h3>
                <p className="text-teal-100/95 max-w-xl leading-relaxed break-words">
                  {primary.description}
                </p>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/95">
                  {primary.program_name && <span className="inline-flex items-center gap-1.5"><Building2 className="h-4 w-4 shrink-0" />{primary.program_name}</span>}
                  {primary.city && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 shrink-0" />{primary.city}</span>}
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 shrink-0" />{primary.duration_weeks} أسبوع</span>
                  <span className="inline-flex items-center gap-1.5 arabic-numerals"><Users className="h-4 w-4 shrink-0" />{primary.seats - (primary.seats_filled || 0)} مقعد متاح</span>
                </div>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <Link
                    to={`/opportunities/${primary.id}`}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-teal-900 font-bold hover:bg-amber-50 focus-visible:ring-4 focus-visible:ring-white/30 transition-all"
                    data-testid="live-opp-primary-cta"
                  >
                    اكتشف الفرصة <ArrowLeft className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </article>

            {peek ? (
              <aside className="lg:col-span-4">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">التالية</div>
                <Link to={`/opportunities/${peek.id}`} className="block rounded-3xl overflow-hidden border border-cream-200 group h-full" data-testid="live-opp-peek">
                  <div className="relative h-48 lg:h-56 overflow-hidden">
                    <img
                      src={peek.cover_image_url || "https://images.unsplash.com/photo-1587560699334-cc4ff634909a?auto=format&fit=crop&w=1200&q=80"}
                      alt={peek.title}
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-transparent" />
                  </div>
                  <div className="p-4">
                    <h4 className="font-heading font-bold text-slate-900 text-lg group-hover:text-teal-700 line-clamp-2">
                      {peek.title}
                    </h4>
                    <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                      {peek.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{peek.city}</span>}
                      <span className="inline-flex items-center gap-1 arabic-numerals"><Clock className="h-3.5 w-3.5" />{peek.duration_weeks} أسبوع</span>
                    </div>
                    <div className="mt-6 inline-flex items-center gap-1.5 text-teal-700 font-semibold text-sm group-hover:gap-2.5 transition-all">
                      اكتشف <ChevronLeft className="h-4 w-4" />
                    </div>
                  </div>
                </Link>
              </aside>
            ) : null}
          </div>
        ) : (
          <div className="p-12 rounded-3xl bg-cream-100/60 border border-cream-200 text-center" data-testid="live-opp-empty">
            <Sparkles className="h-10 w-10 text-slate-400 mx-auto mb-3" />
            <div className="text-lg font-bold text-slate-900">قريباً — فرص جديدة على وشك النشر</div>
            <p className="text-slate-600 mt-2">تفقّد صفحة الفرص خلال أيام لاستكشاف ما هو قادم.</p>
            <Link to="/opportunities" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal-700 text-white font-semibold hover:bg-teal-600">
              افتح صفحة الفرص <ArrowLeft className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
